/**
 * TMRegisterPage — request access to /tm (name, email, phone)
 */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Lock, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '@/config/firebase';
import { registerTMUser } from '@/services/tmService';

const schema = z.object({
  name:     z.string().min(2, 'Name must be at least 2 characters'),
  email:    z.string().email('Enter a valid email'),
  phone:    z.string().min(10, 'Enter a valid phone number'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
});
type FormValues = z.infer<typeof schema>;

export default function TMRegisterPage() {
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors, isSubmitting } } =
    useForm<FormValues>({ resolver: zodResolver(schema) });

  const onSubmit = async (v: FormValues) => {
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, v.email, v.password);
      await updateProfile(cred.user, { displayName: v.name });
      await registerTMUser(cred.user.uid, v.name, v.email, v.phone);
      navigate('/tm/pending');
    } catch (err: unknown) {
      const msg = (err as { code?: string }).code;
      if (msg === 'auth/email-already-in-use') {
        setError('This email is already registered. Try signing in.');
      } else {
        setError('Registration failed. Please try again.');
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 flex flex-col items-center justify-center p-5">

      <div className="text-center mb-8">
        <img src="/angels-army-logo.png" alt="Angels Army" className="w-16 h-16 object-contain mx-auto mb-3" />
        <h1 className="text-xl font-bold text-gray-900">Request Access</h1>
        <p className="text-sm text-gray-500 mt-1">Angels Army Team Manager</p>
      </div>

      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-gray-100 p-7">
        <p className="text-sm text-gray-500 mb-5 bg-indigo-50 rounded-xl px-4 py-3">
          Fill in your details. An admin will review and approve your request.
        </p>

        {error && (
          <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          {[
            { name: 'name',  label: 'Full Name',    icon: User,  type: 'text',     placeholder: 'Your full name' },
            { name: 'email', label: 'Email Address', icon: Mail,  type: 'email',    placeholder: 'you@example.com' },
            { name: 'phone', label: 'Phone / WhatsApp', icon: Phone, type: 'tel',  placeholder: '+91 9876543210' },
          ].map(({ name, label, icon: Icon, type, placeholder }) => (
            <div key={name}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
              <div className="relative">
                <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type={type}
                  placeholder={placeholder}
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 text-sm
                             focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                  {...register(name as keyof FormValues)}
                />
              </div>
              {errors[name as keyof FormValues] && (
                <p className="mt-1 text-xs text-red-500">{errors[name as keyof FormValues]?.message}</p>
              )}
            </div>
          ))}

          {/* Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="Min 6 characters"
                className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 text-sm
                           focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                {...register('password')}
              />
              <button type="button" onClick={() => setShowPw(p => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p className="mt-1 text-xs text-red-500">{errors.password.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold text-sm
                       hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-60"
          >
            {isSubmitting ? 'Submitting…' : 'Request Access'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-5">
          Already have an account?{' '}
          <Link to="/tm" className="text-indigo-600 font-medium hover:underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
